import React, { useEffect, useMemo, useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import OkrPointsRaceBoard from './OkrPointsRaceBoard';

const TEAM_FILTER_ORDER = ['PROYECTOS', 'FINANZAS', 'OPERACIONES', 'AMEVEL', 'VENDING'];

const getId = (value) => value?._id || value?.id || value || '';

const normalizeTeamName = (value) => String(value || '').trim().toUpperCase();

const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString('es-PA', { maximumFractionDigits: 2 }) : '0';
};

const getCycleStatusLabel = (status) => {
    if (status === 'draft') return 'Borrador';
    if (status === 'active') return 'Activo';
    if (status === 'closed') return 'Cerrado';
    return status || '-';
};

const getCycleStatusSeverity = (status) => {
    if (status === 'active') return 'success';
    if (status === 'closed') return 'danger';
    if (status === 'draft') return 'warning';
    return 'info';
};

const getPaceStatus = (actualProgress, expectedProgress, hasData) => {
    if (!hasData || expectedProgress === null || expectedProgress === undefined) return { label: 'Sin datos', severity: 'secondary' };
    return actualProgress >= expectedProgress
        ? { label: 'En ritmo', severity: 'success' }
        : { label: 'Atrasado', severity: 'danger' };
};

const OkrExecutiveDashboard = ({
    selectedCycle,
    scoreboard = [],
    teams = [],
    keyResults = [],
    users = [],
    currentWeek,
    canFilterByTeam = false,
    loading = false
}) => {
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const weeks = useMemo(() => (Array.isArray(selectedCycle?.weeks) ? selectedCycle.weeks : []), [selectedCycle]);
    const pointsBase = Number(selectedCycle?.pointsBase || 1000);
    const numberOfWeeks = Number(selectedCycle?.numberOfWeeks || weeks.length || 0);
    const currentWeekNumber = Number(currentWeek?.weekNumber || 0);
    const expectedProgressPercentage = numberOfWeeks > 0 && currentWeekNumber > 0 ? (currentWeekNumber / numberOfWeeks) * 100 : null;

    const teamFilterOptions = useMemo(() => {
        const optionsById = new Map();
        scoreboard.forEach((row) => {
            const team = row?.team || {};
            const teamId = String(getId(team) || getId(row?.teamId) || '');
            const teamName = team?.name || teams.find((item) => String(getId(item)) === teamId)?.name;
            if (!teamId || !teamName) return;
            optionsById.set(teamId, { label: teamName, value: teamId });
        });

        const options = Array.from(optionsById.values()).sort((left, right) => {
            const leftIndex = TEAM_FILTER_ORDER.indexOf(normalizeTeamName(left.label));
            const rightIndex = TEAM_FILTER_ORDER.indexOf(normalizeTeamName(right.label));
            const leftRank = leftIndex === -1 ? TEAM_FILTER_ORDER.length : leftIndex;
            const rightRank = rightIndex === -1 ? TEAM_FILTER_ORDER.length : rightIndex;
            return leftRank - rightRank || left.label.localeCompare(right.label, 'es');
        });
        return [{ label: 'Todos los equipos', value: '' }, ...options];
    }, [scoreboard, teams]);

    useEffect(() => {
        if (!canFilterByTeam && selectedTeamId) {
            setSelectedTeamId('');
            return;
        }
        if (selectedTeamId && !teamFilterOptions.some((option) => option.value === selectedTeamId)) {
            setSelectedTeamId('');
        }
    }, [canFilterByTeam, selectedTeamId, teamFilterOptions]);

    const visibleScoreboard = useMemo(
        () => !canFilterByTeam || !selectedTeamId
            ? scoreboard.filter(row => !row?.team?.isManagementGroup)
            : scoreboard.filter((row) => String(getId(row?.team) || getId(row?.teamId) || '') === selectedTeamId),
        [canFilterByTeam, scoreboard, selectedTeamId]
    );

    const teamRankingRows = useMemo(
        () =>
            scoreboard.filter(row => selectedTeamId || !row?.team?.isManagementGroup).map((row, index) => {
                const normalizedPoints = Number((row.normalizedTeamPoints ?? row.accumulatedPoints) || 0);
                const progress = Number(row.teamProgressPercentage || 0);
                const hasData = normalizedPoints > 0 || progress > 0;
                const status = getPaceStatus(progress, expectedProgressPercentage, hasData);

                return {
                    ranking: index + 1,
                    teamId: String(getId(row?.team) || getId(row?.teamId) || ''),
                    teamName: row?.team?.name || '-',
                    normalizedPoints,
                    progress,
                    status
                };
            }).filter((row) => !canFilterByTeam || !selectedTeamId || String(getId(row.teamId)) === selectedTeamId),
        [canFilterByTeam, expectedProgressPercentage, scoreboard, selectedTeamId]
    );

    if (!selectedCycle?._id) {
        return (
            <div className="surface-card border-round p-4 shadow-1 text-center">
                <i className="pi pi-chart-line text-4xl text-primary mb-3" />
                <h3 className="mt-0 mb-2">Selecciona o crea un ciclo OKR para ver el dashboard.</h3>
                <p className="text-color-secondary m-0">El tablero Meta 1,000 se genera con datos reales del ciclo seleccionado.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-column gap-3">
            <div className="surface-card border-round p-3 shadow-1">
                <div className="flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3">
                    <div>
                        <h3 className="m-0">Meta 1,000 puntos</h3>
                        <p className="text-color-secondary mt-2 mb-0">Una vista visual del avance de cada colaborador hacia la meta del ciclo.</p>
                    </div>
                    <div className="flex align-items-center flex-wrap gap-2">
                        {canFilterByTeam ? (
                            <div className="flex flex-column gap-1 mr-2" style={{ minWidth: '220px' }}>
                                <label htmlFor="okr-dashboard-team-filter" className="text-sm font-medium">Filtrar por equipo</label>
                                <Dropdown
                                    inputId="okr-dashboard-team-filter"
                                    value={selectedTeamId}
                                    options={teamFilterOptions}
                                    onChange={(event) => setSelectedTeamId(event.value || '')}
                                    placeholder="Todos los equipos"
                                    className="w-full"
                                    aria-label="Filtrar dashboard OKR por equipo"
                                />
                            </div>
                        ) : null}
                        <Tag value={`Ciclo: ${selectedCycle.name || '-'}`} severity="info" />
                        <Tag value={getCycleStatusLabel(selectedCycle.status)} severity={getCycleStatusSeverity(selectedCycle.status)} />
                        <Tag value={currentWeekNumber ? `Semana actual: ${currentWeekNumber}` : 'Semana actual: -'} severity="secondary" />
                    </div>
                </div>
            </div>

            <OkrPointsRaceBoard
                selectedCycle={selectedCycle}
                scoreboard={visibleScoreboard}
                currentWeek={currentWeek}
                teams={teams}
                keyResults={keyResults}
                users={users}
            />

            <div className="surface-card border-round p-3 shadow-1">
                <h3 className="mt-0">Ranking de equipos</h3>
                <p className="text-color-secondary mt-0">El ranking usa avance y puntaje normalizado, no puntos brutos.</p>
                <DataTable value={teamRankingRows} loading={loading} responsiveLayout="scroll" emptyMessage="Sin equipos en el ranking">
                    <Column field="ranking" header="Ranking" sortable style={{ width: '8rem' }} />
                    <Column field="teamName" header="Equipo" sortable style={{ minWidth: '180px' }} />
                    <Column field="progress" header="% avance" body={(row) => `${formatNumber(row.progress)}%`} sortable style={{ minWidth: '120px' }} />
                    <Column field="normalizedPoints" header="Puntaje normalizado" body={(row) => `${formatNumber(row.normalizedPoints)} / ${formatNumber(pointsBase)}`} sortable style={{ minWidth: '180px' }} />
                    <Column header="Estado" body={(row) => <Tag value={row.status.label} severity={row.status.severity} />} sortable style={{ minWidth: '130px' }} />
                </DataTable>
            </div>
        </div>
    );
};

export default OkrExecutiveDashboard;
